import * as React from 'react';
import { useCallback, useEffect } from 'react';

import { useRetracked } from 'js/lib/retracked';
import { useLocation } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import { Button } from '@coursera/cds-core';
import type { ButtonProps, IconProps } from '@coursera/cds-core';
import { ErrorIcon } from '@coursera/cds-icons';

import IdealUpgradeEnrollModal from 'bundles/coursera-plus-common/components/subscriptionTiers/upgradeToPlus/IdealUpgradeEnrollModal';
import IndiaPriceTestUpgradeModal from 'bundles/coursera-plus-common/components/subscriptionTiers/upgradeToPlus/IndiaUpgrade/IndiaPriceTestUpgradeModal';
import { useCourseraPlusSubscriptionFreeTrials } from 'bundles/coursera-plus-common/hooks/useCourseraPlusSubscriptionFreeTrials';
import { useEnrollInOrUpgradeToCourseraPlus } from 'bundles/coursera-plus-common/hooks/useEnrollInOrUpgradeToCourseraPlus';
import { getIsLearnerImpressedInIndiaTiersEpic } from 'bundles/coursera-plus-common/utils/subscriptionTiersUtils';
import { useExistingCourseraLiteSubscription } from 'bundles/coursera-plus-common/utils/withExistingCourseraLiteSubscription';
import { useGetSubscriptionTiersProductPrices } from 'bundles/coursera-plus-landing-page/contentfulData/providerHOC';
import { productPriceToMonetaryAmount } from 'bundles/coursera-plus-landing-page/utils/productPriceToMonetaryAmount';
import { MONTHLY } from 'bundles/subscriptions/common/BillingCycleType';

import _t from 'i18n!nls/coursera-plus';

export type ButtonPropsType = ButtonProps['variant'];

type PropsFromCaller = {
  ctaLabel?: string | null;
  ctaVariant?: ButtonPropsType;
  trackingName: string;
  trackEnrollCtaClick?: () => void;
  ctaIcon?: React.ReactElement<IconProps>;
  shouldCheckActionParams?: boolean;
  // HeroModule and Information do not have the required data to allow the experiment data flow.
  shouldDisableIndiaExperiment?: boolean;
};

const EnrollOrUpgradeToCourseraPlus = ({
  ctaLabel,
  ctaVariant,
  trackingName,
  ctaIcon,
  trackEnrollCtaClick,
  shouldCheckActionParams,
  shouldDisableIndiaExperiment,
}: PropsFromCaller) => {
  const { query } = useLocation();
  const isUserAuthenticated = isAuthenticatedUser();
  const [idealUpgradeModalOpen, setIdealUpgradeModalOpen] = React.useState(false);
  const { existingCourseraLiteSubscription, allSubscriptions } = useExistingCourseraLiteSubscription();
  const isSubscribedToCourseraPlus = allSubscriptions?.some(
    (subscription) =>
      subscription.isCourseraPlusSubscription &&
      (subscription.isActive || subscription.isFreeTrial || subscription.isCancelled)
  );
  const { courseraPlusMonthlyProductPrice } = useGetSubscriptionTiersProductPrices({
    skip: !existingCourseraLiteSubscription?.id,
    shouldGetCourseraPlusAnnual: false,
  });
  const { userHasCourseraPlusFreeTrial } = useCourseraPlusSubscriptionFreeTrials();
  const retrackedTrack = useRetracked();

  const {
    isSubmitting: isEnrollingInCourseraPlus,
    handleEnrollInCourseraPlus,
    error: cPlusenrollmentError,
  } = useEnrollInOrUpgradeToCourseraPlus();

  const enrollOrUpgradeToCourseraPlus = useCallback<() => void>(() => {
    if (existingCourseraLiteSubscription?.id) {
      setIdealUpgradeModalOpen(true);
    } else {
      handleEnrollInCourseraPlus();
    }
  }, [handleEnrollInCourseraPlus, existingCourseraLiteSubscription]);

  // If the user is authenticated and the query param is 'enroll', then enroll the user into Coursera Plus.
  // This is to automatically trigger the enrollment flow after the user logs in.
  useEffect(() => {
    if (shouldCheckActionParams && isUserAuthenticated && query?.action === 'enroll') {
      enrollOrUpgradeToCourseraPlus();
    }
  }, [query, isUserAuthenticated, enrollOrUpgradeToCourseraPlus, shouldCheckActionParams]);

  const onClose = () => {
    setIdealUpgradeModalOpen(false);
  };

  if (isSubscribedToCourseraPlus) {
    return null;
  }

  if (!shouldDisableIndiaExperiment && getIsLearnerImpressedInIndiaTiersEpic()) {
    return <IndiaPriceTestUpgradeModal onClose={onClose} />;
  }

  const handleButtonClick = () => {
    trackEnrollCtaClick?.();

    if (trackingName) {
      retrackedTrack({
        trackingName,
        trackingData: {},
        action: 'click',
      });
    }

    enrollOrUpgradeToCourseraPlus();
  };

  const getButtonIcon = () => {
    if (cPlusenrollmentError) {
      return <ErrorIcon color="error" />;
    } else if (ctaIcon) {
      return ctaIcon;
    }
    return undefined;
  };

  let _ctaLabel = ctaLabel;
  if (existingCourseraLiteSubscription) {
    _ctaLabel = _t('Switch to Coursera Plus');
  } else if (!userHasCourseraPlusFreeTrial) {
    _ctaLabel = _t('Join Coursera Plus');
  }

  return (
    <>
      <Button
        component="button"
        variant={ctaVariant || 'primary'}
        loading={isEnrollingInCourseraPlus}
        icon={getButtonIcon()}
        onClick={handleButtonClick}
      >
        {_ctaLabel}
      </Button>

      {idealUpgradeModalOpen && courseraPlusMonthlyProductPrice && (
        <IdealUpgradeEnrollModal
          courseraPlusProductPrice={productPriceToMonetaryAmount(courseraPlusMonthlyProductPrice)}
          onClose={onClose}
          isStandaloneCourseraPlusUpgrade={true}
          toTierBillingCycle={MONTHLY}
        />
      )}
    </>
  );
};

export default EnrollOrUpgradeToCourseraPlus;
